import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import {
  brand,
  neutral,
  status,
  radius,
  shadow,
  layout,
  fontFamily,
  FONT_SIZE_BASE,
  SPACING_UNIT,
} from './tokens';

export type ColorMode = 'light' | 'dark';
export type Direction = 'ltr' | 'rtl';

function palette(mode: ColorMode): ThemeOptions['palette'] {
  const isLight = mode === 'light';

  return {
    mode,
    primary: {
      main: isLight ? brand[500] : brand[300],
      light: isLight ? brand[300] : brand[200],
      dark: isLight ? brand[700] : brand[500],
      contrastText: isLight ? neutral[0] : neutral[950],
    },
    secondary: {
      main: isLight ? neutral[600] : neutral[300],
      contrastText: isLight ? neutral[0] : neutral[950],
    },
    success: { main: isLight ? status.success.main : status.success.dark },
    warning: { main: isLight ? status.warning.main : status.warning.dark },
    error: { main: isLight ? status.error.main : status.error.dark },
    info: { main: isLight ? status.info.main : status.info.dark },
    background: {
      default: isLight ? neutral[50] : neutral[950],
      paper: isLight ? neutral[0] : neutral[900],
    },
    text: {
      primary: isLight ? neutral[800] : neutral[100],
      secondary: isLight ? neutral[500] : neutral[400],
      disabled: isLight ? neutral[400] : neutral[600],
    },
    divider: isLight ? neutral[200] : neutral[800],
    action: {
      hover: isLight ? 'rgba(78, 116, 229, 0.05)' : 'rgba(139, 165, 240, 0.08)',
      selected: isLight ? 'rgba(78, 116, 229, 0.10)' : 'rgba(139, 165, 240, 0.14)',
    },
  };
}

function typography(): ThemeOptions['typography'] {
  return {
    fontFamily: fontFamily.sans,
    fontSize: FONT_SIZE_BASE,
    // A 6-step scale. Anything not on it is a mistake, not a decision.
    h1: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h2: { fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    h5: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      lineHeight: 1.5,
    },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
  };
}

function components(mode: ColorMode): ThemeOptions['components'] {
  const isLight = mode === 'light';
  const border = isLight ? neutral[200] : neutral[800];

  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Scrollbars styled to match the surface rather than the OS default,
        // which otherwise looks jarring in dark mode.
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: isLight ? neutral[300] : neutral[700],
          borderRadius: radius.pill,
          border: `2px solid ${isLight ? neutral[50] : neutral[950]}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: isLight ? neutral[400] : neutral[600],
        },
        body: { backgroundColor: isLight ? neutral[50] : neutral[950] },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: radius.lg },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${border}`,
          boxShadow: shadow.card,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radius.md, paddingInline: 16, minHeight: 38 },
        sizeSmall: { minHeight: 32, paddingInline: 12 },
        sizeLarge: { minHeight: 44, paddingInline: 20 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: radius.md } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: isLight ? neutral[0] : neutral[950],
        },
        notchedOutline: { borderColor: border },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 500 },
        sizeSmall: { height: 22, fontSize: '0.75rem' },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: isLight ? neutral[800] : neutral[700],
          fontSize: '0.75rem',
          borderRadius: radius.sm,
          paddingBlock: 6,
          paddingInline: 10,
        },
        arrow: { color: isLight ? neutral[800] : neutral[700] },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          border: `1px solid ${border}`,
          boxShadow: shadow.overlay,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.xl, boxShadow: shadow.overlay },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: radius.md, minHeight: 40 },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36, color: 'inherit' } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: border },
        head: {
          fontWeight: 600,
          color: isLight ? neutral[600] : neutral[400],
          backgroundColor: isLight ? neutral[50] : neutral[900],
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: border } } },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: radius.md } },
    },
  };
}

/**
 * Build the portal theme.
 *
 * `direction` must match the Emotion cache in use — see RtlCacheProvider. Setting
 * one without the other produces a layout that is mirrored but with unmirrored
 * padding, which looks broken in a way that is hard to diagnose.
 */
export function createAppTheme(mode: ColorMode = 'light', direction: Direction = 'ltr'): Theme {
  return createTheme({
    direction,
    spacing: SPACING_UNIT,
    shape: { borderRadius: radius.lg },
    palette: palette(mode),
    typography: typography(),
    components: components(mode),
    // Exposed so layout code does not hardcode these numbers.
    custom: { layout, radius, shadow, brand, neutral, status },
  });
}

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      layout: typeof layout;
      radius: typeof radius;
      shadow: typeof shadow;
      brand: typeof brand;
      neutral: typeof neutral;
      status: typeof status;
    };
  }
  interface ThemeOptions {
    custom?: Theme['custom'];
  }
}
